import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  getOtherPartyListReconciliation,
  getReconciliationList,
  ReconcileStatus,
} from "../../actions/settlementActions";
import RemoveRedEyeRoundedIcon from "@mui/icons-material/RemoveRedEyeRounded";
import { useNavigate } from "react-router-dom";
const ReconciliationStatus = () => {
  const dispatch = useDispatch();
  const businessInfo = useSelector((state) => state?.app?.businessInfo);
  const customerId = businessInfo?.customerId;

  const [selectedIds, setSelectedIds] = useState([]);
  const [list, setList] = useState([]);
  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [selectedValue, setSelectedValue] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const navigate = useNavigate();
  const redirect = (row) => {
    navigate(`/app/management/reconciliation-details`, { state: { row } });
  };
  // Fetch party list on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const roleResult = await dispatch(getOtherPartyListReconciliation());
        setList(roleResult || []);
      } catch (error) {
        console.error("Error fetching initial data:", error);
      }
    };
    fetchInitialData();
  }, [dispatch, customerId]);

  const handleReceiverChange = async (event, newValue) => {
    if (newValue) {
      try {
        setLoading(true);
        const data = await dispatch(getReconciliationList(newValue));
        setData(data);
        setSelectedValue(newValue);
      } catch (error) {
        console.error("Error fetching discrepancy list:", error);
      } finally {
        setLoading(false);
      }
    }
  };
  const handleReconcileStatus = async () => {
    try {
      const bapId = selectedValue;
      const data = selectedRows.map(
        (list) => list.orders[0]?.settlements[0]?.id
      );

      const payload = {
        bapId,
        settlementList: data,
      };

      const res = await dispatch(ReconcileStatus(payload));

      if (res?.data?.message) {
        setMsg(res.data.message);
        setDialogOpen(true);
        setTimeout(() => {
          setDialogOpen(false);
          setMsg("");
        }, 3000);
      }
    } catch (error) {
      console.error("Error sending reconcile:", error);
    }
  };

  // DataGrid columns
  const columns = [
    {
      field: "settlementIds",
      headerName: "Settlement IDs",
      flex: 2,
      valueGetter: ({ row }) =>
        Array.isArray(row?.settlements)
          ? row.settlements.map((s) => s.id).join(", ")
          : "",
    },
    { field: "bap_id", headerName: "BAP ID", flex: 1 },
    {
      field: "context",
      headerName: "BPP ID",
      flex: 1,
      valueGetter: ({ row }) => row?.context?.bpp_id || "",
    },
    { field: "collectorAppId", headerName: "Collector App Id", flex: 1.5 },
    {
      field: "reconciliationStatus",
      headerName: "Reconciliation Status",
      flex: 1.5,
    },
    { field: "creationType", headerName: "Creation Type", flex: 1 },
    {
      field: "settlementStatus",
      headerName: "Settlement Status",
      flex: 1,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      valueGetter: ({ row }) =>
        row?.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      valueGetter: ({ row }) =>
        row?.updatedAt ? new Date(row.updatedAt).toLocaleString() : "",
    },
    {
      field: "providerName",
      headerName: "Provider Name",
      flex: 1,
      valueGetter: ({ row }) => row?.provider?.name || "",
    },
    {
      field: "orderId",
      headerName: "Order ID",
      flex: 1,
      valueGetter: ({ row }) => row?.orders?.[0]?.id || "",
    },
    {
      field: "collectedAmount",
      headerName: "Collected Amount",
      flex: 1,
      valueGetter: ({ row }) =>
        row?.orders?.[0]?.collector?.amount?.value || "",
    },
    {
      field: "selfAmount",
      headerName: "Self Amount",
      flex: 1,
      valueGetter: ({ row }) => row?.self?.amount?.value || "",
    },
    {
      field: "discrepancyAmount",
      headerName: "Discrepancy Amount",
      flex: 1,
      valueGetter: ({ row }) =>
        row?.discrepancies?.[0]?.settled_amount?.value || "",
    },
    {
      field: "action",
      disableClickEventBubbling: true,
      headerName: "Action",
      renderCell: (params) => {
        const redirectToView = () => {
          redirect(params.row);
        };

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center", // vertical center alignment
              justifyContent: "flex-start",
              gap: 1,
            }}
          >
            <Tooltip title="View details">
              <Box sx={{ cursor: "pointer" }} onClick={redirectToView}>
                <RemoveRedEyeRoundedIcon />
              </Box>
            </Tooltip>
            {/* <Tooltip title="View details">
                          <Box sx={{ cursor: "pointer" }} onClick={redirectToEdit}>
                            <EditIcon />
                          </Box> 
                        </Tooltip>*/}
          </Box>
        );
      },
      flex: 1,
    },
  ];

  return (
    <Box
      sx={{
        maxWidth: "100%",
        padding: 4,
        borderRadius: 2,
        margin: "auto",
        marginTop: 4,
        backgroundColor: "#fff",
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h3" color="textPrimary">
            Reconciliations
          </Typography>
        </Grid>

        {/* Party Selector */}
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={list}
            value={selectedValue}
            onChange={handleReceiverChange}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField {...params} label="Other Party" fullWidth />
            )}
          />
        </Grid>

        {/* DataGrid */}
        <Grid item xs={12}>
          <div style={{ height: 500, width: "100%" }}>
            <DataGrid
              rows={data}
              columns={columns}
              loading={loading}
              getRowId={(row) => row._id}
              checkboxSelection
              disableRowSelectionOnClick
              selectionModel={selectedIds}
              onSelectionModelChange={(newSelection) => {
                setSelectedIds(newSelection);
                const selectedData = data.filter((row) =>
                  newSelection.includes(row._id)
                );
                setSelectedRows(selectedData);
                console.log("Selected rows:", selectedData);
              }}
            />
          </div>
        </Grid>

        {/* Reconcile Button */}
        <Grid item xs={12} sx={{ textAlign: "left", mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleReconcileStatus}
            disabled={selectedRows.length === 0}
          >
            Reconcile Status
          </Button>
        </Grid>
      </Grid>

      {/* Dialog for messages */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>Reconcile</DialogTitle>
        <DialogContent>
          <Typography>{msg}</Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReconciliationStatus;
